import { cn } from '@myosh/odin-components';
import type { Node, NodeProps } from '@xyflow/react';
import type React from 'react';
import { useMemo } from 'react';
import { makeDataCy } from '../../../helpers/util';
import { useAppSelector } from '../../../redux/hooks';
import {
  selectConsequencesLength,
  selectControlsLengthByConsequenceId,
  selectMoving,
} from '../../../redux/slices/diagram';
import BaseNode, { type BaseNodeData } from './base-node.component';
import type { HandlesProps } from './common/handles.component';
import VerticalMoveControl, { VERTICAL_STEP, VerticalMoveDirection } from './common/vertical-move-control.component';

type ConsequenceNodeData = BaseNodeData;
export type ConsequenceNodeType = Node<ConsequenceNodeData>;
type ConsequenceNodeProps = NodeProps<ConsequenceNodeType>;

const className = 'bg-consequences-box-bg border-consequences-box-border text-consequences-box-fg';
const iconClassName = 'text-consequences-box-bg stroke-consequences-box-fg';
const handles: HandlesProps = { leftHandleId: 'left', leftHandleType: 'source' };
const staticData = { className, iconClassName, handles };

/**
 * A component that represents a consequence node in a flow diagram.
 * Extends the base node component with consequence-specific functionality.
 *
 * @component
 * @param {ConsequenceNodeProps} props - The properties for the consequence node component
 * @returns {JSX.Element} A rendered consequence node component
 *
 * @example
 * ```tsx
 * <ConsequenceNode
 *   id="consequence-1"
 *   data={{ label: "Root Consequence" }}
 *   position={{ x: 100, y: 100 }}
 * />
 * ```
 */
const ConsequenceNode = (props: ConsequenceNodeProps): React.JSX.Element => {
  // derive a slug from the node label for a stable selector
  const dataCyLabel = useMemo(() => makeDataCy(props.data.label ?? ''), [props.data.label]);

  const consequencesLength = useAppSelector(selectConsequencesLength);
  const controlsLength = useAppSelector((state) => selectControlsLengthByConsequenceId(state, props.data.recordId!));

  const moving = useAppSelector(selectMoving);
  const { rowIndex } = props.data;

  const canMoveUp = useMemo(() => (rowIndex !== undefined ? rowIndex >= VERTICAL_STEP : false), [rowIndex]);

  const canMoveDown = useMemo(
    () => (rowIndex !== undefined ? rowIndex < consequencesLength - VERTICAL_STEP : false),
    [rowIndex, consequencesLength]
  );

  const hasControls = controlsLength > 0;

  const data = useMemo(() => {
    const className = cn(staticData.className, { 'animate-wobble': moving && (canMoveUp || canMoveDown) });
    return {
      ...props.data,
      ...staticData,
      className,
      showUnlinkActionIcon: !props.data.recordId || !hasControls,
    };
  }, [props.data, canMoveUp, canMoveDown, moving]);

  return (
    <div data-cy={`parent-consequence-node-container-${dataCyLabel}`} className="group flex items-center">
      <VerticalMoveControl
        id={props.id}
        type="consequence-node"
        index={data.rowIndex!}
        title="Move Up"
        direction={VerticalMoveDirection.UP}
        disabled={!moving || !canMoveUp}
      />

      <BaseNode {...props} data={data} />

      <VerticalMoveControl
        id={props.id}
        type="consequence-node"
        index={data.rowIndex!}
        title="Move Down"
        direction={VerticalMoveDirection.DOWN}
        disabled={!moving || !canMoveDown}
      />
    </div>
  );
};

export default ConsequenceNode;
